import { falAngleLeft } from 'shared/@generated/icons/falAngleLeft';
import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falTimes } from 'shared/@generated/icons/falTimes';
import { forwardRef, Fragment, PropsWithChildren, ReactNode, useState } from 'react';
import { Pressable, ScrollView, TouchableOpacity, View } from 'react-native';
import clsx from 'clsx';
import { BaseModalRef } from 'shared/components/base-modal';
import { Button } from 'shared/components/button';
import { Slider } from 'shared/components/form/slider';
import { FullscreenModal } from 'shared/components/fullscreen-modal';
import { FaIcon, Icon } from 'shared/components/icon';
import { Text } from 'shared/components/text';

export type FilterOption<T> = {
  label: string;
  value: T | null;
  icon?: ReactNode;
};

export function FilterButtonRow({ children }: PropsWithChildren) {
  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={false}
      className={'-mx-3 md:mx-0'}
      contentContainerClassName={'gap-2 px-3 md:flex-wrap md:px-0'}
    >
      {children}
    </ScrollView>
  );
}

export function FilterButton({
  label,
  icon,
  count = 0,
  active,
  testID,
  onPress,
}: {
  label: string;
  icon?: FaIcon;
  count?: number;
  active?: boolean;
  testID?: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      className={clsx(
        'h-10 flex-row items-center gap-2 rounded-full border bg-white px-4',
        active
          ? 'border-brand-brown-dark/60 hover:border-brand-brown-dark'
          : 'border-brand-brown-dark/30 hover:border-brand-brown-dark/60',
      )}
      testID={testID}
      accessibilityRole={'button'}
      onPress={onPress}
    >
      {icon && <Icon icon={icon} className={'text-brand-brown-dark size-4'} />}
      <Text className={'text-brand-brown-dark font-semibold'} numberOfLines={1}>
        {label}
      </Text>
      {count > 0 && (
        <View className={'bg-brand-brown-dark min-w-5 items-center rounded-full px-1.5 py-0.5'}>
          <Text className={'text-xs font-bold text-white'}>{count}</Text>
        </View>
      )}
    </Pressable>
  );
}

export type ActiveFilterItem = {
  key: string;
  label: string;
  onRemove: () => void;
};

export function ActiveFilterList({
  items,
  clearLabel,
  onClear,
}: {
  items: ActiveFilterItem[];
  clearLabel: string;
  onClear: () => void;
}) {
  if (items.length === 0) return null;

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={false}
      className={'-mx-3 mt-3 md:mx-0'}
      contentContainerClassName={'items-center gap-2 px-3 md:flex-wrap md:px-0'}
    >
      {items.map((item) => (
        <ActiveFilterChip key={item.key} label={item.label} onRemove={item.onRemove} />
      ))}
      <TouchableOpacity className={'px-1.5 py-2'} accessibilityRole={'button'} onPress={onClear}>
        <Text className={'text-brand-brown-dark text-sm font-semibold underline'}>
          {clearLabel}
        </Text>
      </TouchableOpacity>
    </ScrollView>
  );
}

export function ActiveFilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <TouchableOpacity
      className={'bg-brand-brown-dark flex-row items-center gap-2 rounded-full px-3 py-2'}
      accessibilityLabel={label}
      accessibilityRole={'button'}
      onPress={onRemove}
    >
      <Text className={'text-sm font-semibold text-white'}>{label}</Text>
      <Icon icon={falTimes} className={'size-3.5 text-white'} />
    </TouchableOpacity>
  );
}

export function FilterOverviewRow({
  label,
  value,
  active,
  testID,
  onPress,
}: {
  label: string;
  value: string;
  active: boolean;
  testID?: string;
  onPress: () => void;
}) {
  return (
    <TouchableOpacity
      className={clsx(
        'hover:bg-brand-cream border-brand-brown-dark/10 -mx-2 flex-row items-center gap-2 border-b px-2 py-3 transition-colors',
      )}
      testID={testID}
      accessibilityRole={'button'}
      onPress={onPress}
    >
      <Text className={'text-brand-brown-dark min-w-28 flex-none md:min-w-32'}>{label}</Text>
      <View className={'min-w-0 flex-1 items-end pl-4'}>
        <Text
          className={clsx(
            'text-brand-brown-dark w-full text-right text-sm',
            active && 'font-semibold',
          )}
          numberOfLines={1}
          ellipsizeMode={'tail'}
        >
          {value}
        </Text>
      </View>
      <Icon icon={falAngleRight} className={'text-brand-brown-dark/60 size-4 flex-none'} />
    </TouchableOpacity>
  );
}

export const FilterModal = forwardRef<
  BaseModalRef,
  PropsWithChildren<{
    title: string;
    overview: boolean;
    backLabel: string;
    onBack: () => void;
    footer: (props: BaseModalRef) => ReactNode;
    contentTestID?: string;
    className?: string;
    onClose?: () => void;
    onRequestClose?: () => boolean | void;
  }>
>(
  (
    {
      title,
      overview,
      backLabel,
      onBack,
      footer,
      contentTestID,
      className,
      onClose,
      onRequestClose,
      children,
    },
    ref,
  ) => (
    <FullscreenModal
      ref={ref}
      centered={true}
      scrollable={false}
      animationInTiming={160}
      animationOutTiming={80}
      className={className}
      contentTestID={contentTestID}
      onClose={onClose}
      onRequestClose={onRequestClose}
      headerTitle={title}
      headerLeft={
        overview ? undefined : () => <FilterModalBackButton label={backLabel} onPress={onBack} />
      }
      footer={footer}
    >
      {children}
    </FullscreenModal>
  ),
);

FilterModal.displayName = 'FilterModal';

export function FilterModalBackButton({
  label,
  testID,
  onPress,
}: {
  label: string;
  testID?: string;
  onPress: () => void;
}) {
  return (
    <TouchableOpacity
      className={
        'hover:bg-brand-cream-dark flex-row items-center gap-1 rounded p-2 transition-colors'
      }
      accessibilityRole={'button'}
      testID={testID}
      onPress={onPress}
    >
      <Icon icon={falAngleLeft} className={'text-brand-brown-dark size-3.5'} />
      <Text className={'text-brand-brown-dark font-semibold'}>{label}</Text>
    </TouchableOpacity>
  );
}

export function FilterModalFooter({
  clearLabel,
  applyLabel,
  resultCount,
  clearDisabled,
  applyDisabled,
  applyLoading,
  clearTestID,
  applyTestID,
  onClear,
  onApply,
}: {
  clearLabel: string;
  applyLabel: string;
  resultCount?: number;
  clearDisabled?: boolean;
  applyDisabled?: boolean;
  applyLoading?: boolean;
  clearTestID?: string;
  applyTestID?: string;
  onClear: () => void;
  onApply: () => void;
}) {
  return (
    <View className={'flex-row gap-3'}>
      <Button
        testID={clearTestID}
        text={clearLabel}
        color={'white'}
        disabled={clearDisabled}
        onPress={onClear}
      />
      <View className={'flex-1'}>
        <Button
          testID={applyTestID}
          text={applyLabel}
          center={true}
          disabled={applyDisabled}
          loading={applyLoading}
          onPress={onApply}
        >
          {resultCount !== undefined && (
            <Text className={'ml-2 rounded bg-black/10 px-2 py-0.5 text-sm font-black text-white'}>
              {resultCount}
            </Text>
          )}
        </Button>
      </View>
    </View>
  );
}

export function SliderFilterPanel({
  caption,
  valueLabel,
  marks,
  action,
  value,
  min,
  max,
  roundTo,
  suffix,
  accessibilityLabel,
  disabled,
  onChange,
}: {
  caption?: string;
  valueLabel: string;
  marks: { value: number; label: string }[];
  action?: ReactNode;
  value: number | null;
  min: number;
  max: number;
  roundTo: number;
  suffix: string;
  accessibilityLabel: string;
  disabled?: boolean;
  onChange: (value: number | null) => void;
}) {
  // Small phones (≈360 px) leave too little room between the last two labels at text-xs.
  const [compactMarks, setCompactMarks] = useState(false);

  return (
    <View>
      <View className={'mb-6 items-center'}>
        {caption && (
          <Text className={'text-brand-brown-dark/60 mb-1 text-sm font-semibold'}>{caption}</Text>
        )}
        <View className={'flex-row items-center gap-2'}>
          <Text
            className={clsx(
              'text-2xl font-black',
              disabled ? 'text-brand-brown-dark/40' : 'text-brand-brown-dark',
            )}
          >
            {valueLabel}
          </Text>
          {action}
        </View>
      </View>
      {/* Labels centre on their ticks; the last one ends at the inset edge so it can't collide with its neighbour or the panel edge. */}
      <View className={'px-4'}>
        <Slider
          value={value}
          min={min}
          max={max}
          roundTo={roundTo}
          suffix={suffix}
          accessibilityLabel={accessibilityLabel}
          disabled={disabled}
          onChange={onChange}
        />
        <View
          className={clsx('relative mt-1 h-8', disabled && 'opacity-40')}
          aria-hidden={true}
          onLayout={(event) => setCompactMarks(event.nativeEvent.layout.width < 320)}
        >
          {marks.map((mark, index) => {
            const left = `${((mark.value - min) / (max - min)) * 100}%` as const;
            const isLast = index === marks.length - 1;

            return (
              <Fragment key={mark.value}>
                <View
                  className={'bg-brand-brown-dark/25 absolute top-0 h-1.5 w-px'}
                  style={{ left }}
                />
                <Text
                  className={clsx(
                    'text-brand-brown-dark/60 absolute top-2.5 font-semibold',
                    compactMarks ? 'text-2xs' : 'text-xs',
                    isLast ? '-right-4 text-right' : 'w-16 -translate-x-1/2 text-center',
                  )}
                  style={isLast ? undefined : { left }}
                >
                  {mark.label}
                </Text>
              </Fragment>
            );
          })}
        </View>
      </View>
    </View>
  );
}

export function SelectFilterPanel<T>({
  options,
  value,
  onChange,
}: {
  options: FilterOption<T>[];
  value: T | null;
  onChange: (value: T | null) => void;
}) {
  return (
    <ScrollView contentContainerClassName={'px-3 py-2 md:px-5'}>
      {options.map((option) => {
        const selected = option.value === value;

        return (
          <Pressable
            key={String(option.value)}
            className={
              'hover:bg-brand-cream -mx-2 min-h-12 flex-row items-center gap-3 rounded-lg px-2 py-3 transition-colors'
            }
            aria-checked={selected}
            accessibilityRole={'radio'}
            accessibilityState={{ checked: selected }}
            onPress={() => onChange(option.value)}
          >
            <View
              className={clsx(
                'size-5 items-center justify-center rounded-full border',
                selected
                  ? 'border-brand-brown-dark bg-brand-brown-dark'
                  : 'border-brand-brown-dark/30 bg-white',
              )}
            >
              {selected && <View className={'size-2 rounded-full bg-white'} />}
            </View>
            {option.icon}
            <Text className={'text-brand-brown-dark flex-1'}>{option.label}</Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}
